import { useRef } from 'react';
import { Camera, X } from 'lucide-react';

// Converts any selected image to a standardized, compressed JPEG before
// it's ever uploaded. This solves two problems at once:
// 1. Format issues (HEIC from iPhone camera, inconsistent PNG extensions)
// 2. File size issues (camera photos can be several MB — way more than
//    needed for a vehicle inspection photo, and can exceed server upload limits)
function convertToJpeg(file, maxDimension = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      let { width, height } = img;

      // Scale down proportionally if the image is larger than maxDimension
      // on its longest side — a vehicle inspection photo doesn't need to be
      // full camera resolution (often 3000-4000px+) to be useful.
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          if (blob) {
            const convertedFile = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
            resolve(convertedFile);
          } else {
            reject(new Error('Conversion failed.'));
          }
        },
        'image/jpeg',
        quality // 0.8 = 80% quality, a good balance of size vs visual quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load image for conversion.'));
    };

    img.src = objectUrl;
  });
}

function PhotoSlot({ imageUrl, onChange, onRemove }) {
  const inputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const jpegFile = await convertToJpeg(file);
      const previewUrl = URL.createObjectURL(jpegFile);
      onChange(jpegFile, previewUrl);
    } catch (err) {
      const previewUrl = URL.createObjectURL(file);
      onChange(file, previewUrl);
    }
  };

  return (
    <div style={styles.slot} onClick={() => !imageUrl && inputRef.current.click()}>
      {imageUrl ? (
        <>
          <img src={imageUrl} alt="vehicle" style={styles.image} />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            style={styles.removeBtn}
          >
            <X size={14} color="white" />
          </button>
        </>
      ) : (
        <Camera size={26} color="#9ca3af" />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
}

const styles = {
  slot: {
    width: '100%',
    aspectRatio: '1',
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
    border: '1px dashed #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: 'rgba(0,0,0,0.6)',
    border: 'none',
    borderRadius: 999,
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};

export default PhotoSlot;